import { useCallback, useEffect, useRef } from "react"
import { lactateAPI } from "@/model/NativeBridge/api"
import { addLogDebug, addLogError } from "@/model/logger"
import { useLactateWorkoutImport } from "./useLactateWorkoutImport"
import { importKeyFor, loadImportedKeys, persistImportedKeys } from "./autoImportedStore"

/**
 * Background auto-import of EN605 lactate measurements into matching workouts.
 *
 * Runs app-wide (mounted once, high in the tree). When the auto-sync toggle is
 * on (default) AND the currently-connected lactometer is an EN605, it pulls the
 * device's cached measurements and attaches each new one to its matching
 * workout via [useLactateWorkoutImport]. Eaglenos-001 is intentionally skipped —
 * that family stays manual-only.
 *
 * Idempotency: every imported measurement is remembered in localStorage
 * (`id:timestamp`), so a measurement is never attached twice. Measurements with
 * no matching workout are left unmarked and retried on a later sweep (e.g. once
 * the workout is created).
 *
 * `active` gates the whole thing (false on web / non-native) — the hook is
 * always called (React rule) but does nothing when inactive.
 */
export function useLactateAutoImport(active: boolean): void {
  const { importMeasurement, flushBackgroundRefresh } = useLactateWorkoutImport({ isBackground: true })
  const sweepingRef = useRef(false)
  const pendingRef = useRef(false)
  const importedRef = useRef<Set<string>>(loadImportedKeys())

  const sweep = useCallback(async () => {
    // Coalesce overlapping triggers into one in-flight sweep + at most one rerun.
    if (sweepingRef.current) {
      pendingRef.current = true
      return
    }
    sweepingRef.current = true
    try {
      const { enabled } = await lactateAPI.getAutoSyncStatus()
      if (!enabled) return

      // Only auto-import from a live EN605. listDevices requires the lactateV2
      // native capability; on older builds it throws and we bail (caught below).
      const { devices } = await lactateAPI.listDevices()
      const online = devices.find((d) => d.online)
      if (online?.family !== "EN605") return

      // Read the device's CACHE — not getMeasurements, which triggers a fresh
      // device sync. Since a completed sync fires dataTransferComplete → this
      // very sweep, getMeasurements here created an endless re-sync loop (the
      // "stuck syncing" bug). The on-connect sync keeps the cache fresh.
      const { measurements } = await lactateAPI.getDeviceMeasurements(online.address)
      let changed = false
      for (const m of measurements) {
        const key = importKeyFor(m)
        if (importedRef.current.has(key)) continue
        const result = await importMeasurement(m)
        if (result.outcome === "imported") {
          importedRef.current.add(key)
          changed = true
          addLogDebug("lactate auto-import: attached measurement", key)
        }
      }
      if (changed) persistImportedKeys(importedRef.current)
    } catch (error) {
      addLogError("lactate auto-import sweep failed", error)
    } finally {
      // One collapse and one refresh for the whole sweep, however many
      // measurements it attached — and whatever went wrong after them: a throw
      // from persisting the keys used to drop the invalidation for every day
      // the sweep had already written.
      flushBackgroundRefresh()
      sweepingRef.current = false
      if (pendingRef.current) {
        pendingRef.current = false
        void sweep()
      }
    }
  }, [importMeasurement, flushBackgroundRefresh])

  useEffect(() => {
    if (!active) return

    let disposed = false
    const unsubs: (() => Promise<void>)[] = []

    const setup = async () => {
      try {
        const onMeasurement = await lactateAPI.onMeasurement(() => void sweep())
        const onComplete = await lactateAPI.onEvent("lactate.dataTransferComplete", () => void sweep())
        if (disposed) {
          onMeasurement().catch(() => {})
          onComplete().catch(() => {})
          return
        }
        unsubs.push(onMeasurement, onComplete)
      } catch (error) {
        addLogError("lactate auto-import: failed to subscribe", error)
      }
    }
    void setup()

    // Catch measurements pulled before we subscribed (e.g. background sync that
    // completed while this runner wasn't mounted).
    void sweep()

    return () => {
      disposed = true
      unsubs.forEach((u) => {
        u().catch(() => {})
      })
    }
  }, [active, sweep])
}
